import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TARIFA } from '../data';
import type { AppState, Sucursal } from '../types';
import { PageShell, StatCard, T, glassLight, inputStyle, primaryButton, ghostButton, useIsMobile, usePagination, Pagination, Modal } from '../ui';
import { claveContador, contadoresUsados, hoyRD, lecturasConConsumo, nombreContador, selloDeLectura, ultimaLecturaDe } from '../contadores';
const money = (n: number) => 'RD$ ' + Math.round(n).toLocaleString('es-DO');
const kwhFmt = (n: number) => Math.round(n).toLocaleString('es-DO') + ' kWh';
const monthLabel = (ts: number) => new Date(ts).toLocaleDateString('es-DO', {
  month: 'short',
  year: '2-digit'
});
// Fecha + hora en horario de República Dominicana (America/Santo_Domingo).
const drDateTime = (ts: number) => new Date(ts).toLocaleString('es-DO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Santo_Domingo'
});
interface Props {
  state: AppState;
  sucursal: Sucursal;
  sucursalesDisponibles: Sucursal[];
  puntoFijo: boolean;
  mostrarCosto?: boolean;
  onChangeSucursal: (id: string) => void;
  // El contador va como texto: se escribe a mano en cada lectura, igual que
  // el número del medidor. No hay catálogo que mantener.
  onSave: (sucursalId: string, contador: string, fecha: number, kwh: number) => void;
  onDelete?: (id: string) => void;
  canArchive?: boolean;
}
export function ConsumoEnergetico({
  state,
  sucursal,
  sucursalesDisponibles,
  puntoFijo,
  mostrarCosto = true,
  onChangeSucursal,
  onSave,
  onDelete,
  canArchive
}: Props) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  // La fecha que propone el formulario es HOY EN RD. Antes salía de
  // `toISOString()`, que da la fecha en UTC: de las 8 de la noche en adelante
  // proponía el día siguiente.
  const [fechaStr, setFechaStr] = useState(() => hoyRD());
  const [lectura, setLectura] = useState('');

  // ── EL CONTADOR ───────────────────────────────────────────────────────
  // Se escribe a mano, como la lectura. El mismo campo hace de filtro: lo que
  // se ve abajo (números, gráfico e historial) es lo de ese contador. En
  // blanco se ve el punto comercial entero.
  const [contador, setContador] = useState('');
  // El aviso lleva un contador de intentos para que dos avisos con el mismo
  // texto sean objetos distintos: si no, React no re-renderiza y el temporizador
  // del primero apaga el segundo a media frase.
  const [aviso, setAviso] = useState<{
    texto: string;
    intento: number;
  } | null>(null);
  const intentos = useRef(0);
  const avisar = (texto: string) => {
    intentos.current += 1;
    setAviso({
      texto,
      intento: intentos.current
    });
  };
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 4200);
    return () => clearTimeout(t);
  }, [aviso]);
  const consumosDelPunto = useMemo(() => state.consumos.filter(c => c.sucursalId === sucursal.id), [state.consumos, sucursal.id]);
  // Los contadores que ya aparecen en las lecturas de este punto. Se ofrecen
  // como sugerencia para que no haya que escribirlos completos cada vez —y,
  // sobre todo, para que no se escriban distinto y partan el historial.
  const usados = useMemo(() => contadoresUsados(state.consumos, sucursal.id), [state.consumos, sucursal.id]);
  // Al cambiar de punto comercial, el contador escrito deja de valer.
  useEffect(() => {
    setContador('');
  }, [sucursal.id]);
  const filtrando = claveContador(contador);
  const viendoTodos = !filtrando;

  // La serie que se mira. El consumo de cada lectura SIEMPRE se calcula
  // contador por contador (ver src/contadores.ts): al ver "todos" se juntan
  // series ya calculadas, no se restan lecturas de medidores distintos.
  const series = useMemo(() => {
    const todas = lecturasConConsumo(consumosDelPunto);
    return viendoTodos ? todas : todas.filter(c => claveContador(c.contador) === filtrando);
  }, [consumosDelPunto, filtrando, viendoTodos]);
  const historial = useMemo(() => [...series].reverse().filter(c => tab === 'activos' ? !c.archivado : c.archivado), [series, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems: histItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(historial);
  const ultima = series[series.length - 1];
  const consumoActual = ultima?.consumo ?? 0;
  const now = new Date();
  const inMonth = (ts: number, offset: number) => {
    const d = new Date(ts);
    const ref = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
  };
  const consumoMes = series.filter(c => inMonth(c.fecha, 0)).reduce((a, c) => a + c.consumo, 0);
  const consumoMesAnterior = series.filter(c => inMonth(c.fecha, 1)).reduce((a, c) => a + c.consumo, 0);
  const costoMes = consumoMes * TARIFA;
  const variacion = consumoMesAnterior > 0 ? Math.round((consumoMes - consumoMesAnterior) / consumoMesAnterior * 100) : null;
  // Últimos 6 meses para el gráfico
  const chartData = series.filter(c => c.consumo > 0).slice(-6);
  const maxConsumo = Math.max(1, ...chartData.map(c => c.consumo));

  // La lectura anterior contra la que se valida es la del contador que está
  // escrito, NO la última que se ve en pantalla: sin filtro, la de arriba
  // puede ser de otro medidor y compararlas no significa nada.
  const anterior = useMemo(() => contador.trim() ? ultimaLecturaDe(consumosDelPunto, sucursal.id, contador) : undefined, [consumosDelPunto, sucursal.id, contador]);
  const ultimaLectura = anterior?.kwh;
  // De qué hablan los números que se ven.
  const ambito = contador.trim() ? nombreContador(contador) : usados.length > 1 ? `Todos los contadores (${usados.length})` : 'Este punto comercial';

  // Columnas del historial. La del contador solo aparece cuando se están
  // mirando varios: con uno escrito, repetir su nombre en cada renglón es
  // ruido. Se declaran una vez y de aquí salen la cabecera y cada fila.
  const mostrarColContador = viendoTodos && usados.length > 0;
  //
  // Las anchuras van como `minmax(0, Nfr)` y no como `Nfr` a secas: un `fr`
  // pelado no baja de lo que ocupa su contenido, así que la columna de las
  // fechas —el texto más largo— se llevaba más de su parte, y como la
  // cabecera y los renglones tienen contenidos distintos, cada rejilla
  // repartía diferente. Con `minmax(0, …)` el reparto es puro porcentaje y
  // todas coinciden.
  const columnasHistorial = [{
    k: 'fecha',
    h: 'Fecha y hora',
    w: 'minmax(0, 1.6fr)'
  }, ...(mostrarColContador ? [{
    k: 'contador',
    h: 'Contador',
    w: 'minmax(0, 1fr)'
  }] : []), {
    k: 'lectura',
    h: 'Lectura',
    w: 'minmax(0, 1fr)'
  }, {
    k: 'consumo',
    h: 'Consumo',
    w: 'minmax(0, 1fr)'
  }, ...(mostrarCosto ? [{
    k: 'costo',
    h: 'Costo',
    w: 'minmax(0, 1fr)'
  }] : []), ...(onDelete ? [{
    k: 'accion',
    // ANCHO FIJO, no `auto`. Aquí estaba el descuadre: la cabecera y cada
    // renglón son rejillas SEPARADAS, así que `auto` no medía lo mismo en
    // todas —en la cabecera esta celda va vacía y en los renglones lleva un
    // botón—, y esa diferencia se repartía entre las columnas `fr`,
    // corriendo los títulos respecto a sus datos.
    //
    // Da para "Desarchivar", que es el botón más ancho.
    h: '',
    w: isMobile ? '104px' : '118px'
  }] : [])];
  const gridHistorial: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: columnasHistorial.map(c => c.w).join(' '),
    // En el teléfono la tabla se desliza de lado: se le da el ancho mínimo
    // para que la fecha con su hora no quede partida en tres renglones.
    minWidth: isMobile ? mostrarColContador ? 640 : 520 : undefined
  };
  const guardar = () => {
    // Lo primero: de cuál contador es. Es el dato que decide de qué serie
    // forma parte la lectura, así que sin él no hay nada que guardar.
    const nombre = contador.trim();
    if (!nombre) {
      avisar('Escribe de cuál contador es la lectura.');
      return;
    }
    const kwh = parseFloat(lectura);
    if (Number.isNaN(kwh) || kwh < 0) {
      avisar('Escribe la lectura del medidor en kWh.');
      return;
    }
    // El medidor es acumulativo: una lectura menor que la anterior es inválida
    // y no se permite registrar.
    if (ultimaLectura !== undefined && kwh < ultimaLectura) {
      avisar(`La lectura (${kwhFmt(kwh)}) no puede ser menor que la anterior de ${nombre} (${kwhFmt(ultimaLectura)}). El medidor es acumulativo: escribe un valor igual o mayor.`);
      return;
    }
    // La fecha que se eligió, con la HORA EXACTA de RD en que se registró.
    onSave(sucursal.id, nombre, selloDeLectura(fechaStr), kwh);
    // Los dos campos quedan en blanco: el número del contador cambia de una
    // lectura a otra, y dejarlo puesto es la forma más fácil de anotarle una
    // lectura al medidor equivocado sin darse cuenta.
    setLectura('');
    setContador('');
  };
  return <PageShell title="Control de Consumo Energético" sub="Registra la lectura de tu medidor. El sistema calcula el resto." data-id="element-428" data-mp="36w656ku0amjv">
      
      <style data-id="element-429" data-mp="3fvjg7hxs3j6p">{`
        .ce-bar { transition: height .4s cubic-bezier(.2,.8,.2,1); }
        .ce-save:hover { opacity:.9; transform: translateY(-1px); }
        .ce-save { transition: all .15s; }
      `}</style>

      {/* ── Tarjeta principal de registro ── */}
      <div style={{
      background: 'linear-gradient(135deg, rgba(255,107,1,.08), rgba(255,107,1,.02))',
      border: `1px solid rgba(255,107,1,.25)`,
      borderRadius: 20,
      padding: isMobile ? 18 : 26
    }} data-id="element-430" data-mp="2n3zrxjmbrexu">
        
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 22
      }} data-id="element-431" data-mp="1zhb6iny3x4ch">
          
          <div data-id="element-432" data-mp="3qcdxzbkdljg3">
            <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.accent,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            marginBottom: 6
          }} data-id="element-433" data-mp="3qauhpkypz8eb">
              
              Punto Comercial
            </div>
            {puntoFijo ? <div style={{
            fontSize: 22,
            fontWeight: 800,
            color: T.text,
            letterSpacing: '-0.02em'
          }} data-id="element-434" data-mp="84om3tfcevyi">
              
                {sucursal.nombre}
              </div> : <select value={sucursal.id} onChange={e => onChangeSucursal(e.target.value)} style={{
            ...inputStyle,
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? 0 : 220,
            fontSize: isMobile ? 16 : 18,
            fontWeight: 700,
            padding: '8px 12px'
          }} data-id="element-435" data-mp="3bmznu18z6xzn">
              
                {sucursalesDisponibles.map(s => <option key={s.id} value={s.id} data-id="element-436" data-mp="2suxelaynyjzf">
                    {s.nombre}
                  </option>)}
              </select>}
            <div style={{
            fontSize: 12,
            color: T.textSecondary,
            marginTop: 5
          }} data-id="element-437" data-mp="2lamrt6lo5mph">
              
              {sucursal.direccion}
            </div>
          </div>
          {ultimaLectura !== undefined && <div style={{
          textAlign: 'right'
        }} data-id="element-438" data-mp="26g87n2qdc798">

              <div style={{
            fontSize: 11,
            color: T.textMuted,
            fontWeight: 600
          }} data-id="element-439" data-mp="22dv2ogjg4ddi">

                Última lectura
              </div>
              <div style={{
            fontSize: 16,
            fontWeight: 700,
            color: T.text,
            marginTop: 2
          }} data-id="element-440" data-mp="1xl1xjrk2f30w">

                {kwhFmt(ultimaLectura)}
              </div>
              <div style={{
            fontSize: 11,
            color: T.textMuted,
            marginTop: 2
          }} data-id="element-441" data-mp="1nn4a36a15a9c">

                {nombreContador(contador)}
              </div>
            </div>}
        </div>

        <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr 1fr auto',
        gap: 14,
        alignItems: 'end',
        maxWidth: 940
      }} data-id="element-442" data-mp="143f7n2e86ag2">

          {/* El contador va PRIMERO: es lo que decide de qué medidor es la
           lectura, y sin eso el número que se escriba no significa nada. Se
           escribe a mano, como la lectura; la lista de abajo son los que ya
           se han usado en este punto, para no teclearlos completos ni
           escribirlos distinto (que partiría el historial en dos). */}
          <div data-id="element-443" data-mp="1ruud341at7vj">
            <label style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            color: T.accent,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            marginBottom: 7
          }} data-id="element-444" data-mp="1e1cublgfrz6k">

              Contador
            </label>
            <input list="ce-contadores-usados" value={contador} onChange={e => setContador(e.target.value)} placeholder="Ej: Oficina, Planta, Nº AB-77123" style={{
            ...inputStyle,
            fontWeight: 600,
            borderColor: contador.trim() ? 'rgba(255,107,1,.45)' : undefined
          }} onKeyDown={e => {
            if (e.key === 'Enter') guardar();
          }} data-id="element-445" data-mp="2bgqqtawallmq" />

            <datalist id="ce-contadores-usados" data-id="element-446" data-mp="2un6lqr187wqu">
              {usados.map(u => <option key={u} value={u} data-id="element-447" data-mp="2pmsng0pfg160" />)}
            </datalist>
          </div>
          <div data-id="element-448" data-mp="21xpwu3ctexzd">
            <label style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            marginBottom: 7
          }} data-id="element-449" data-mp="21x6pfgksjrzl">

              Fecha
            </label>
            <input type="date" value={fechaStr} onChange={e => setFechaStr(e.target.value)} style={inputStyle} data-id="element-450" data-mp="26u6oh7j83uh7" />
            
          </div>
          <div data-id="element-451" data-mp="3ib6nsn3zn9kq">
            <label style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            marginBottom: 7
          }} data-id="element-452" data-mp="3ibg6rqqm8ggx">
              
              Lectura del Medidor (kWh)
            </label>
            <input type="number" min="0" inputMode="decimal" value={lectura} onChange={e => setLectura(e.target.value)} placeholder="Ej: 290500" style={inputStyle} onKeyDown={e => {
            if (e.key === 'Enter') guardar();
          }} data-id="element-453" data-mp="21qfm0u1i0ol4" />
            
          </div>
          <button className="ce-save" onClick={guardar} style={{
          ...primaryButton,
          padding: '12px 22px',
          fontSize: 14,
          height: 44,
          width: isMobile ? '100%' : 'auto'
        }} data-id="element-454" data-mp="2fqqaxrj8zt3n">
            
            Guardar Consumo
          </button>
        </div>

        {/* El aviso se re-monta con cada intento (la clave lleva el número),
         así vuelve a aparecer aunque el texto sea el mismo. */}
        {aviso && <div key={aviso.intento} style={{
        marginTop: 12,
        padding: '10px 14px',
        borderRadius: 10,
        background: T.warnSoft,
        border: `1px solid ${T.warn}`,
        color: T.warn,
        fontSize: 13,
        fontWeight: 600
      }} data-id="element-455" data-mp="2yfwwdmbfeyt5">

            {aviso.texto}
          </div>}

        <div style={{
        fontSize: 12,
        color: T.textMuted,
        marginTop: 12
      }} data-id="element-456" data-mp="8vekeuplbc43">

          Escribe de cuál contador es y el número que muestra ese medidor. Se
          guarda con la fecha y la hora exactas de República Dominicana, y el
          sistema calcula el consumo y el costo automáticamente.
        </div>
      </div>

      {/* ── Resultados ── */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))',
      gap: 12
    }} data-id="element-457" data-mp="wqr2r08ne4v0">
        
        <StatCard label="Consumo actual" value={kwhFmt(consumoActual)} note={`Última lectura · ${ambito}`} accent={T.info} data-id="element-458" data-mp="1p9mu3op9hqyb" />

        <StatCard label="Consumo del mes" value={kwhFmt(consumoMes)} note={variacion === null ? `Mes en curso · ${ambito}` : `${variacion >= 0 ? '▲' : '▼'} ${Math.abs(variacion)}% vs mes anterior`} accent={variacion !== null && variacion > 0 ? T.warn : T.ok} data-id="element-459" data-mp="1kh3kf2tp3fys" />
        
        {mostrarCosto && <StatCard label="Costo estimado" value={money(costoMes)} note={`Tarifa interna ${TARIFA} RD$/kWh`} accent="#c4b5fd" data-id="element-460" data-mp="1ultobe8nsu5n" />}
      </div>

      {/* ── Gráfico de consumo mensual ── */}
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: T.textMuted,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      marginTop: 8,
      marginBottom: 4
    }} data-id="element-461" data-mp="1ko5q2ebuh4iq">
        
        Consumo mensual
      </div>
      <div style={{
      ...glassLight,
      padding: isMobile ? 16 : 24
    }} data-id="element-462" data-mp="1bptwq4jlfsml">
        
        {chartData.length === 0 ? <div style={{
        textAlign: 'center',
        color: T.textMuted,
        fontSize: 13,
        padding: '24px 0'
      }} data-id="element-463" data-mp="361s21ves84d7">
          
            Aún no hay suficientes lecturas para graficar.
          </div> : <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: isMobile ? 8 : 18,
        height: isMobile ? 160 : 200,
        padding: '0 4px'
      }} data-id="element-464" data-mp="yfiaflv274ii">
          
            {chartData.map(c => {
          const h = Math.round(c.consumo / maxConsumo * 160) + 4;
          const cost = c.consumo * TARIFA;
          return <div key={c.id} style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }} data-id="element-465" data-mp="13ds4ft51rz2j">
                
                  <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: T.text
            }} data-id="element-466" data-mp="1d70kl318m4ty">
                  
                    {Math.round(c.consumo / 1000)}k
                  </div>
                  <div className="ce-bar" title={`${kwhFmt(c.consumo)} · ${money(cost)}`} style={{
              width: '100%',
              maxWidth: 54,
              height: h,
              borderRadius: '8px 8px 0 0',
              background: 'linear-gradient(180deg,#FF8C00,#FF5A00)',
              boxShadow: '0 0 18px rgba(255,107,1,.35)'
            }} data-id="element-467" data-mp="3svsgh193wsk3" />
                
                  <div style={{
              fontSize: 11,
              color: T.textMuted,
              fontWeight: 600
            }} data-id="element-468" data-mp="3btldjtna84ph">
                  
                    {monthLabel(c.fecha)}
                  </div>
                </div>;
        })}
          </div>}
      </div>

      {/* ── Comparación ── */}
      <div style={{
      ...glassLight,
      padding: 20,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap'
    }} data-id="element-469" data-mp="2o3r0baz1we4p">
        
        <div data-id="element-470" data-mp="2y2q167dkr5h5">
          <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: T.text
        }} data-id="element-471" data-mp="2y2gi73qy5yky">
            
            Comparación con el mes anterior
          </div>
          <div style={{
          fontSize: 12,
          color: T.textSecondary,
          marginTop: 3
        }} data-id="element-472" data-mp="dbg330w0bn5w">
            
            Mes actual {kwhFmt(consumoMes)} · Mes anterior{' '}
            {kwhFmt(consumoMesAnterior)}
          </div>
        </div>
        <div style={{
        background: variacion !== null && variacion > 0 ? T.warnSoft : T.okSoft,
        color: variacion !== null && variacion > 0 ? T.warn : T.ok,
        borderRadius: 99,
        padding: '8px 16px',
        fontWeight: 700,
        fontSize: 14
      }} data-id="element-473" data-mp="1sx04m1q1u9k2">
          
          {variacion === null ? 'Sin referencia' : `${variacion >= 0 ? '+' : ''}${variacion}%`}
        </div>
      </div>

      {/* ── Historial ── */}
      <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 8,
      marginBottom: 4
    }} data-id="element-474" data-mp="154jed3w5rb9o">
        
        <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: T.textMuted,
        letterSpacing: '.1em',
        textTransform: 'uppercase'
      }} data-id="element-475" data-mp="2zjdj6niuejqu">
          
          Historial de registros
        </div>
        <div style={{
        display: 'flex',
        gap: 8
      }} data-id="element-476" data-mp="3nv3psfepgmgf">
          
          <button onClick={() => {
          setTab('activos');
          setPage(1);
        }} style={{
          ...ghostButton,
          padding: '4px 8px',
          fontSize: 11,
          background: tab === 'activos' ? T.accentSoft : 'transparent',
          color: tab === 'activos' ? T.accent : T.textMuted
        }} data-id="element-477" data-mp="3iv8yiar0puuu">
            
            Activos
          </button>
          {canArchive && <button onClick={() => {
          setTab('archivados');
          setPage(1);
        }} style={{
          ...ghostButton,
          padding: '4px 8px',
          fontSize: 11,
          background: tab === 'archivados' ? T.accentSoft : 'transparent',
          color: tab === 'archivados' ? T.accent : T.textMuted
        }} data-id="element-478" data-mp="304q6bda6rvxg">
            
              Archivados
            </button>}
        </div>
      </div>
      <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-479" data-mp="pe9knqwixkq2">
        
        <div style={{
        overflowX: 'auto'
      }} data-id="element-480" data-mp="27bctu9du7a89">
          
          <div style={{
          ...gridHistorial,
          background: T.surfaceSubtle,
          borderBottom: `1px solid ${T.border}`
        }} data-id="element-481" data-mp="2c9xxyjw7me1z">

            {columnasHistorial.map((col, i) => <div key={col.k} style={{
            padding: '12px 16px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: T.textMuted,
            textAlign: i === 0 || col.k === 'contador' ? 'left' : 'right'
          }} data-id="element-482" data-mp="2h2qzz00an8ad" data-mpi={i}>

                {col.h}
              </div>)}
          </div>
          {series.length === 0 && <div style={{
          padding: '28px 16px',
          textAlign: 'center',
          color: T.textMuted,
          fontSize: 13
        }} data-id="element-483" data-mp="1xf00xdq490ek">

              Sin registros aún
            </div>}
          {histItems.map((c, i, arr) => <div key={c.id} style={{
          ...gridHistorial,
          borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
          alignItems: 'center'
        }} data-id="element-484" data-mp="3dq41qx1dnvbs" data-mpi={i}>

              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.text,
            fontWeight: 600
          }} data-id="element-485" data-mp="19nlqp55f4xxe">

                {drDateTime(c.fecha)}
              </div>
              {mostrarColContador && <div style={{
            padding: '13px 16px',
            fontSize: 12,
            color: T.textSecondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} data-id="element-486" data-mp="1ioa2dt7cam74">

                  {nombreContador(c.contador)}
                </div>}
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.textSecondary,
            textAlign: 'right'
          }} data-id="element-487" data-mp="14paggtb7olen">

                {kwhFmt(c.kwh)}
              </div>
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.text,
            fontWeight: 600,
            textAlign: 'right'
          }} data-id="element-488" data-mp="78lgl55tnt7w">
              
                {c.consumo > 0 ? kwhFmt(c.consumo) : '—'}
              </div>
              {mostrarCosto && <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.accent,
            fontWeight: 600,
            textAlign: 'right'
          }} data-id="element-489" data-mp="29v8y3e50jsy0">
              
                  {c.consumo > 0 ? money(c.consumo * TARIFA) : '—'}
                </div>}
              {onDelete && <div style={{
            padding: '8px 16px',
            textAlign: 'right'
          }} data-id="element-490" data-mp="36l9jr4a35tb3">
              
                  <button onClick={() => setConfirm(c.id)} style={{
              ...ghostButton,
              padding: '4px 8px',
              fontSize: 11,
              color: c.archivado ? T.info : T.warn,
              borderColor: c.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
              background: c.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
            }} data-id="element-491" data-mp="2smjlhlbj5nl5">
                
                    {c.archivado ? 'Desarchivar' : 'Archivar'}
                  </button>
                </div>}
            </div>)}
          {series.length > 0 && <div style={{
          padding: '4px 16px 14px'
        }} data-id="element-492" data-mp="1rpi1rmib3994">
            
              <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="registros" data-id="element-493" data-mp="1rn5hv3szmotf" />
            
            </div>}
        </div>
      </div>

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-494" data-mp="2fekrlhn3g8ep">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-495" data-mp="2fd9jj5mm8ehs">
          
            ¿Estás seguro de que deseas{' '}
            {state.consumos.find(c => c.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            este registro de consumo?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-496" data-mp="uyg1xj0x86a3">
          
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-497" data-mp="36ofhcfthvjor">
            
              Cancelar
            </button>
            <button onClick={() => {
          if (onDelete) onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: state.consumos.find(c => c.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: state.consumos.find(c => c.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-498" data-mp="3flpvc8xp5mxd">
            
              Sí,{' '}
              {state.consumos.find(c => c.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}

    </PageShell>;
}